import React, { useState, useEffect, useRef } from 'react';
import ArtifactVisualizer from './visualization/ArtifactVisualizer';
import { TemplateVisualizer } from './visualization';
import WebSearchResults from './WebSearchResults';
import MathSolution from './MathSolution';
import axios from 'axios';
import './VisualizationCanvas.css';

/**
 * VisualizationCanvas - A sliding full-screen visualization component that
 * displays interactive educational visualizations based on conversation topics.
 * 
 * This component slides in from the right when a visualization is triggered
 * and takes over the full screen to display the visualization.
 */
const VisualizationCanvas = ({ 
  visualizationData, 
  isVisible, 
  onClose,
  onError,
  artifactId // New prop for artifact ID from artifacts-service
}) => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visualizationMeta, setVisualizationMeta] = useState({});
  const [localArtifactId, setLocalArtifactId] = useState(artifactId); // Local state for artifact ID
  const canvasRef = useRef(null);
  const abortControllerRef = useRef(null);
  const cooldownRef = useRef(false);
  
  // Effect to update localArtifactId when the prop changes
  useEffect(() => {
    if (artifactId) {
      setLocalArtifactId(artifactId);
    }
  }, [artifactId]);

  // Effect to handle visualization when data changes
  useEffect(() => {
    if (visualizationData && isVisible) {
      // Ensure canvas is visible immediately
      if (canvasRef.current) {
        canvasRef.current.classList.add('visible');
        
        // Force styling for visibility
        setTimeout(() => {
          if (canvasRef.current) {
            canvasRef.current.classList.add('visible', 'force-visible');
          }
        }, 50);
      }
      
      // Check if this is web search results (new content type)
      if (visualizationData.type === 'web_search_results') {
        setVisualizationMeta({
          title: visualizationData.title || `Search Results for "${visualizationData.query}"`,
          description: `Found ${visualizationData.total_results} results`,
          topic: visualizationData.query || "search",
          contentType: 'web_search_results'
        });
        setLoading(false);
        return;
      }
      
      // Check if this is math solution (new content type)
      if (visualizationData.type === 'math_solution') {
        setVisualizationMeta({
          title: visualizationData.title || `Math Solution: ${visualizationData.problem}`,
          description: `${visualizationData.subject} • ${visualizationData.taxonomy_level} level`,
          topic: visualizationData.problem || "math",
          contentType: 'math_solution'
        });
        setLoading(false);
        return;
      }
      
      // Check if this is Socratic math session (new content type)
      if (visualizationData.type === 'socratic_math') {
        setVisualizationMeta({
          title: visualizationData.title || `Let's Explore: ${visualizationData.problem}`,
          description: `${visualizationData.subject} • Interactive Discovery`,
          topic: visualizationData.problem || "math",
          contentType: 'socratic_math'
        });
        setLoading(false);
        return;
      }
      
      // Check if this is a template visual (fast path — no artifact generation needed)
      if (visualizationData.type === 'template_visual') {
        setVisualizationMeta({
          title: visualizationData.title || visualizationData.topic,
          description: `${visualizationData.subject} visualization`,
          topic: visualizationData.topic,
          contentType: 'template_visual'
        });
        setLoading(false);
        return;
      }

      // Check if this is an artifact visual from Tier 3 codegen (arrives via data channel)
      if (visualizationData.type === 'artifact_visual') {
        setLocalArtifactId(visualizationData.artifactId);
        setVisualizationMeta({
          title: visualizationData.title || 'Interactive Visualization',
          description: `${visualizationData.subject} visualization`,
          topic: visualizationData.topic,
          contentType: 'artifact_visual'
        });
        setLoading(false);
        return;
      }

      // First check if we have an artifactId directly from props
      if (localArtifactId) {
        setLoading(false);
        return;
      }

      // Check if visualization data has an artifact ID
      if (visualizationData.artifactId) {
        setLocalArtifactId(visualizationData.artifactId);
        setVisualizationMeta({
          title: visualizationData.title || "Interactive Visualization",
          description: visualizationData.description || "",
          topic: visualizationData.topic || "",
          ...visualizationData.meta
        });
        setLoading(false);
        return;
      }
      
      // If we don't have an artifact ID, generate one through the API
      generateVisualization(visualizationData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualizationData, isVisible, localArtifactId]);
  
  // Handle animation timing when visibility changes
  useEffect(() => {
    if (canvasRef.current) {
      if (isVisible) {
        // Add a tiny delay to ensure DOM updates before animation
        setTimeout(() => {
          canvasRef.current.classList.add('visible');
        }, 50);
      } else {
        canvasRef.current.classList.remove('visible', 'force-visible');
      }
    }
  }, [isVisible]);
  
  // Additional effect to ensure visualization shows even if DOM updates slowly
  useEffect(() => {
    if (isVisible && canvasRef.current) {
      // Double-check visibility class after a longer delay
      setTimeout(() => {
        if (!canvasRef.current.classList.contains('visible')) {
          canvasRef.current.classList.add('visible');
        }
      }, 500);
    }
  }, [isVisible, visualizationData]);
  
  // Abort in-flight request and clean up on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Function to generate visualization using artifacts-service API via Flask proxy
  const generateVisualization = async (data) => {
    // Rate-limit: skip if already loading or in cooldown
    if (loading || cooldownRef.current) {
      return;
    }

    // Abort any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      // Clean up context to ensure it's a proper string
      let contextToSend = "";
      if (data.context) {
        if (typeof data.context === 'string') {
          contextToSend = data.context;
        } else if (typeof data.context === 'object') {
          try {
            contextToSend = JSON.stringify(data.context);
          } catch (e) {
            console.warn('Failed to stringify context object:', e);
            contextToSend = String(data.context);
          }
        } else {
          contextToSend = String(data.context);
        }
      }

      // Call the artifacts-service API via Flask proxy
      const response = await axios.post('/api/generate', {
        prompt: data.topic,
        context: contextToSend,
        visualization_type: data.visualizationType || 'custom',
        data: data.data || {}
      }, {
        timeout: 45000,
        signal: controller.signal,
      });

      if (response.data && response.data.success && response.data.artifact_id) {
        // Make sure canvas is visible
        if (canvasRef.current) {
          canvasRef.current.classList.add('visible', 'force-visible');
        }

        // Store the artifact ID
        setLocalArtifactId(response.data.artifact_id);
        setVisualizationMeta({
          title: data.topic || "Interactive Visualization",
          topic: data.topic,
          context: contextToSend,
          ...response.data.metadata
        });
      } else {
        throw new Error(response.data?.error || "Failed to generate artifact");
      }
    } catch (err) {
      // Don't set error state for intentional cancellations
      if (axios.isCancel(err) || err.name === 'AbortError') {
        return;
      }
      console.error("Error generating visualization:", err);
      setError(err.message || "Failed to generate visualization");
      if (onError) onError(err);
    } finally {
      setLoading(false);
      // 3-second cooldown to prevent rapid-fire requests
      cooldownRef.current = true;
      setTimeout(() => {
        cooldownRef.current = false;
      }, 3000);
    }
  };
  
  
  // Get panel type label for context
  const getPanelTypeLabel = () => {
    if (visualizationMeta.contentType === 'web_search_results') return 'Search Results';
    if (visualizationMeta.contentType === 'math_solution') return 'Math Solution';
    if (visualizationMeta.contentType === 'socratic_math') return 'Interactive Math';
    if (visualizationMeta.contentType === 'template_visual') return 'Visual';
    if (visualizationMeta.contentType === 'artifact_visual') return 'Interactive';
    return 'Visualization';
  };

  // Truncate title for display
  const getDisplayTitle = () => {
    const title = visualizationMeta.title || visualizationData?.topic || 'Interactive Visualization';
    if (title.length > 40) {
      return title.substring(0, 37) + '...';
    }
    return title;
  };

  return (
    <div
      ref={canvasRef}
      className={`visualization-canvas ${isVisible ? 'preparing' : ''}`}
    >
      {/* Header */}
      <div className="visualization-header">
        <div className="header-content">
          <span className="panel-type-label">{getPanelTypeLabel()}</span>
          <h2>{getDisplayTitle()}</h2>
        </div>
        <button
          onClick={onClose}
          className="close-button"
          aria-label="Close panel"
          title="Close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      
      {/* Content area */}
      <div className="visualization-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner" />
            <p>Generating interactive visualization for {visualizationData?.topic || "your topic"}...</p>
            <small>This may take a moment as we create a custom visualization experience.</small>
          </div>
        ) : error ? (
          <div className="error-container">
            <h3>Error Creating Visualization</h3>
            <p>{error}</p>
            <p>Please try again or ask for a different visualization.</p>
          </div>
        ) : visualizationMeta.contentType === 'web_search_results' ? (
          // Display web search results
          <WebSearchResults searchData={visualizationData} />
        ) : (visualizationMeta.contentType === 'math_solution' || visualizationMeta.contentType === 'socratic_math') ? (
          // Display math (both solution and Socratic modes)
          <MathSolution mathData={visualizationData} />
        ) : visualizationMeta.contentType === 'template_visual' ? (
          // Display template-based visualization (fast path)
          <TemplateVisualizer templateData={visualizationData} />
        ) : localArtifactId ? (
          // Using artifacts-service with ArtifactVisualizer
          <ArtifactVisualizer
            artifactId={localArtifactId}
            width="100%"
            height="100%"
            onError={(err) => {
              setError(err.message || "Failed to load visualization");
            }}
          />
        ) : (
          <div className="empty-container">
            <p>No artifact ID available for {visualizationData?.topic || "this topic"}</p>
            <button 
              onClick={() => generateVisualization(visualizationData)}
              className="retry-button"
              style={{
                marginTop: '20px',
                padding: '8px 16px',
                backgroundColor: '#297a92',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
      
      {/* Optional footer with metadata */}
      {visualizationMeta.description && (
        <div className="visualization-footer">
          <p>{visualizationMeta.description}</p>
        </div>
      )}
    </div>
  );
};

export default VisualizationCanvas;